/* eslint-disable no-nested-ternary */
import { PropTypes } from 'prop-types'
import React, { useState, useRef, useEffect } from 'react'
import './UITextInput.scss'

const UITextInput = (props) => {
  const {
    id,
    type,
    step,
    value,
    onChange,
    autoFocus,
    error,
    success,
    isRequired,
    prefix,
    unit,
    placeHolder,
    disabled,
    shiny,
    style,
    styleContainer,
    styleInput,
    stylePrefix,
    autocompleteOptions,
    maxAutocompleteResults,
    onAutocompleteSelect,
    autoExpand,
    minWidth,
    maxWidth,
  } = props

  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const [filteredOptions, setFilteredOptions] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [inputWidth, setInputWidth] = useState(minWidth || 'auto')
  const inputRef = useRef(null)
  const autocompleteRef = useRef(null)
  const measureRef = useRef(null)

  // Fonction pour mesurer la largeur du texte et ajuster l'input
  const updateInputWidth = () => {
    if (!autoExpand || !measureRef.current || !inputRef.current) return

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    const computedStyle = window.getComputedStyle(inputRef.current)
    
    // Copie des styles de l'input pour mesurer précisément
    context.font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`
    
    const textToMeasure = value || placeHolder || ''
    const textWidth = context.measureText(textToMeasure).width
    
    // Ajoute de la padding pour l'espacement interne
    const padding = 24 // Ajustez selon vos styles
    const calculatedWidth = textWidth + padding
    
    // Applique les contraintes min/max
    let finalWidth = Math.max(calculatedWidth, minWidth || 100)
    if (maxWidth) {
      finalWidth = Math.min(finalWidth, maxWidth)
    }
    
    setInputWidth(finalWidth)
  }

  // Met à jour la largeur quand la valeur change
  useEffect(() => {
    if (autoExpand) {
      updateInputWidth()
    }
  }, [value, autoExpand, minWidth, maxWidth])

  // Met à jour la largeur au montage du composant
  useEffect(() => {
    if (autoExpand) {
      // Petite pause pour s'assurer que les styles sont appliqués
      setTimeout(updateInputWidth, 0)
    }
  }, [autoExpand])

  const inputTextInput = `uiInput_field
  ${success ? ' uiInput_field--success' : ''}
  ${error ? ' uiInput_field--error' : ''}
  ${shiny ? ' uiInput_field--shiny' : ''}
  `

  // Styles pour l'input avec auto-expansion
  const inputStyles = {
    ...styleInput,
    ...(autoExpand && { 
      width: inputWidth,
      minWidth: minWidth || '100px',
      maxWidth: maxWidth || 'none',
      transition: 'width 0.2s ease'
    })
  }
  const parseAddressInput = (input) => {
    const trimmedInput = input.trim()
    // Regex pour capturer le numéro au début (peut inclure bis, ter, etc.)
    const numberMatch = trimmedInput.match(/^(\d+(?:\s*(?:bis|ter|quater|a|b|c))?)\s+(.+)$/i)
    
    if (numberMatch) {
      return {
        number: numberMatch[1].trim(),
        address: numberMatch[2].trim()
      }
    }
    
    // Si pas de numéro détecté, tout est considéré comme adresse
    return {
      number: null,
      address: trimmedInput
    }
  }

  // Filtrer les options d'autocomplétion
  useEffect(() => {
    if (autocompleteOptions && value && value.length > 0) {
      const { number, address } = parseAddressInput(value)
      
      // Si on a un numéro, on cherche seulement dans la partie adresse
      const searchTerm = number ? address : value
      
      if (searchTerm.length > 0) {
        const filtered = autocompleteOptions
          .filter(option => {
            // Si l'utilisateur a saisi un numéro, on cherche dans l'adresse sans le numéro
            const optionToSearch = number ? 
              option.replace(/^\d+(?:\s*(?:bis|ter|quater|a|b|c))?\s+/i, '') : 
              option
            
            return optionToSearch.toLowerCase().includes(searchTerm.toLowerCase())
          })
          .map(option => {
            // Si l'utilisateur a un numéro, on remplace le numéro de l'option par celui saisi
            if (number) {
              const optionWithoutNumber = option.replace(/^\d+(?:\s*(?:bis|ter|quater|a|b|c))?\s+/i, '')
              return `${number} ${optionWithoutNumber}`
            }
            return option
          })
          .slice(0, maxAutocompleteResults)
        
        setFilteredOptions(filtered)
        setShowAutocomplete(filtered.length > 0 && !filtered.some(opt => opt === value))
        setSelectedIndex(-1)
      } else {
        setShowAutocomplete(false)
        setFilteredOptions([])
      }
    } else {
      setShowAutocomplete(false)
      setFilteredOptions([])
    }
  }, [value, autocompleteOptions, maxAutocompleteResults])

  // Gérer la sélection d'une option
  const handleOptionSelect = (option) => {
    onChange(option)
    setShowAutocomplete(false)
    if (onAutocompleteSelect) {
      onAutocompleteSelect(option)
    }
  }

  // Gérer les événements clavier
  const handleKeyDown = (e) => {
    if (type === 'number' && e.key === ',') {
      e.preventDefault()
      document.execCommand('insertText', false, '.')
      return
    }

    if (!showAutocomplete) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0) {
          handleOptionSelect(filteredOptions[selectedIndex])
        }
        break
      case 'Escape':
        setShowAutocomplete(false)
        setSelectedIndex(-1)
        break
      default:
        break
    }
  }

  // Gérer le collage d'une valeur avec virgule décimale dans un champ numérique
  const handlePaste = (e) => {
    if (type !== 'number') return
    const pasted = e.clipboardData.getData('text')
    if (pasted.includes(',')) {
      e.preventDefault()
      document.execCommand('insertText', false, pasted.replace(/,/g, '.'))
    }
  }

  return (
    <div className="uiInput" style={style}>
      {prefix && (
        <h2 className="uiInput_prefix" style={stylePrefix}>
          {isRequired ? `${prefix} *:` : `${prefix} :`}
        </h2>
      )}
      <div className="uiInput_container" style={styleContainer}>
        <div className="uiInput_inputWrapper" style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            className={inputTextInput}
            style={inputStyles}
            id={id}
            type={type}
            step={step}
            value={value}
            onChange={(e) => {
              onChange(e.target.value)
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            autoFocus={autoFocus}
            placeholder={placeHolder}
            readOnly={disabled}
            autoComplete="off"
          />

          {/* Élément invisible pour mesurer le texte */}
          {autoExpand && (
            <span
              ref={measureRef}
              style={{
                position: 'absolute',
                visibility: 'hidden',
                whiteSpace: 'pre',
                fontSize: 'inherit',
                fontFamily: 'inherit',
                padding: '0',
                border: '0',
                pointerEvents: 'none'
              }}
            >
              {value || placeHolder}
            </span>
          )}
          
          {/* Liste d'autocomplétion */}
          {showAutocomplete && (
            <div 
              ref={autocompleteRef}
              className="uiInput_autocomplete"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderTop: 'none',
                borderRadius: '0 0 4px 4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto'
              }}
            >
              {filteredOptions.map((option, index) => (
                <div
                  key={option}
                  className={`uiInput_autocomplete-option ${
                    index === selectedIndex ? 'uiInput_autocomplete-option--selected' : ''
                  }`}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: index === selectedIndex ? '#f0f0f0' : 'transparent',
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #eee' : 'none'
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault() // Empêche le blur de l'input
                    handleOptionSelect(option)
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {unit && (
          <div className="uiInput_containerUnit">
            <h2 className="uiInput_unit">{unit}</h2>
          </div>
        )}
      </div>
    </div>
  )
}

UITextInput.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string.isRequired,
  step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.string,
  onChange: PropTypes.func,
  autoFocus: PropTypes.bool,
  error: PropTypes.bool,
  success: PropTypes.bool,
  isRequired: PropTypes.bool,
  prefix: PropTypes.string,
  unit: PropTypes.string,
  placeHolder: PropTypes.string,
  disabled: PropTypes.bool,
  shiny: PropTypes.bool,
  style: PropTypes.shape({}),
  styleContainer: PropTypes.shape({}),
  styleInput: PropTypes.shape({}),
  stylePrefix: PropTypes.shape({}),
  styleAutoComplete: PropTypes.shape({}),
  autocompleteOptions: PropTypes.arrayOf(PropTypes.string),
  maxAutocompleteResults: PropTypes.number,
  onAutocompleteSelect: PropTypes.func,
  autoExpand: PropTypes.bool,
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

UITextInput.defaultProps = {
  id: null,
  step: null,
  onChange: null,
  autoFocus: false,
  error: false,
  success: false,
  isRequired: false,
  prefix: null,
  unit: null,
  placeHolder: null,
  disabled: false,
  shiny: false,
  style: null,
  styleContainer: null,
  styleInput: null,
  stylePrefix: null,
  styleAutoComplete: null,
  autocompleteOptions: null,
  maxAutocompleteResults: 10,
  onAutocompleteSelect: null,
  autoExpand: false,
  minWidth: null,
  maxWidth: null,
}

export default UITextInput